import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth, UserButton } from "@clerk/react";
import { toast } from "sonner";
import { Building2, Users, Flame, CheckCircle2, Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API, authed } from "@/lib/api";
import { StatCard } from "@/pages/AdminDashboard";
import LeadsTable from "@/components/admin/LeadsTable";
import LeadDrawer from "@/components/admin/LeadDrawer";
import SchoolDialog from "@/components/super/SchoolDialog";
import SchoolRow from "@/components/super/SchoolRow";

const th = "text-left font-medium px-4 py-3";

export default function SuperDashboard() {
  const { getToken } = useAuth();
  const [schools, setSchools] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");
  const [dialog, setDialog] = useState({ open: false, school: null });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const opts = await authed(getToken);
      const [s, st] = await Promise.all([axios.get(`${API}/super/schools`, opts), axios.get(`${API}/super/stats`, opts)]);
      setSchools(s.data);
      setStats(st.data);
    } catch {
      setSchools((p) => p || []);
      toast.error("Could not load schools");
    }
  }, [getToken]);

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/super/leads`, { params: filter ? { school_id: filter } : {}, ...(await authed(getToken)) });
      setLeads(data);
    } catch {
      setLeads((p) => p || []);
      toast.error("Could not load enquiries");
    }
  }, [getToken, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { document.title = "Platform admin — Schools"; }, []);

  const patch = async (s, body, msg) => {
    try {
      const { data } = await axios.patch(`${API}/super/schools/${s.id}`, body, await authed(getToken));
      setSchools((list) => list.map((x) => (x.id === s.id ? data : x)));
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (s) => {
    try {
      await axios.delete(`${API}/super/schools/${s.id}`, await authed(getToken));
      toast.success(`${s.config.institute_name} deleted`);
      load();
      loadLeads();
    } catch {
      toast.error("Delete failed");
    }
  };

  const root = stats?.root_domain || "preschoolplatform.com";

  return (
    <div className="min-h-screen bg-stone-50" data-testid="super-dashboard">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div>
            <p className="font-display font-semibold text-lg leading-tight">Preschool<span className="text-brand">Platform</span></p>
            <p className="text-xs text-stone-500">Super admin</p>
          </div>
          <div data-testid="super-user-button"><UserButton /></div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">All schools</h1>
            <p className="text-sm text-stone-500 mt-1">Create preschool websites, control access and see every enquiry.</p>
          </div>
          <button onClick={() => setDialog({ open: true, school: null })} className="flex items-center gap-2 px-5 py-2.5 rounded-full btn-brand text-sm font-semibold" data-testid="add-school-button">
            <Plus className="w-4 h-4" /> Add school
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Building2} label="Schools" value={stats?.schools} testid="stat-schools" tone="bg-brand-soft text-brand" />
          <StatCard icon={CheckCircle2} label="Live websites" value={stats?.active_schools} testid="stat-active-schools" tone="bg-green-100 text-green-700" />
          <StatCard icon={Users} label="Total enquiries" value={stats?.total_leads} testid="stat-total-leads" tone="bg-accent-soft text-stone-800" />
          <StatCard icon={Flame} label="Hot this week" value={stats?.hot_leads_this_week} testid="stat-hot-leads" tone="bg-red-100 text-red-600" />
        </div>

        <Tabs defaultValue="schools">
          <TabsList className="mb-4">
            <TabsTrigger value="schools" data-testid="tab-schools">Schools</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-all-leads">All enquiries</TabsTrigger>
          </TabsList>
          <TabsContent value="schools">
            <div className="bg-white rounded-2xl border overflow-x-auto">
              <table className="w-full text-sm" data-testid="schools-table">
                <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide">
                  <tr><th className={th}>School</th><th className={th}>Web address</th><th className={`${th} text-center`}>Leads</th><th className={th}>Status</th><th className={`${th} text-right`}>Actions</th></tr>
                </thead>
                <tbody>
                  {(schools || []).map((s) => (
                    <SchoolRow key={s.id} s={s} rootDomain={root} onEdit={(x) => setDialog({ open: true, school: x })} onDelete={remove}
                      onToggle={(x, v) => patch(x, { active: v }, v ? "Website is live" : "Website paused")} />
                  ))}
                </tbody>
              </table>
              {schools && schools.length === 0 && <p className="text-center text-sm text-stone-500 py-14" data-testid="schools-empty-state">No schools yet. Add your first preschool.</p>}
              {!schools && <p className="text-center text-sm text-stone-400 py-14">Loading schools…</p>}
            </div>
          </TabsContent>
          <TabsContent value="leads">
            <LeadsTable leads={leads} onSelect={setSelected} showSchool>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border px-3 py-1.5 text-sm bg-white" data-testid="leads-school-filter">
                <option value="">All schools</option>
                {(schools || []).map((s) => <option key={s.id} value={s.id}>{s.config.institute_name}</option>)}
              </select>
            </LeadsTable>
          </TabsContent>
        </Tabs>
      </main>
      <SchoolDialog open={dialog.open} school={dialog.school} rootDomain={root} onClose={() => setDialog((d) => ({ ...d, open: false }))} onSaved={() => load()} />
      <LeadDrawer lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
